'use client';
import React, { useEffect, useCallback, useState, useRef } from 'react';
import Script from 'next/script';

const GOOGLE_CLIENT_ID = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          prompt: (cb?: (notification: any) => void) => void;
          cancel: () => void;
        };
      };
    };
  }
}

export default function OneTap() {
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const promptCalledRef = useRef(false);

  const isLoggedIn = () => {
    if (typeof window === 'undefined') return false;
    return Boolean(localStorage.getItem('jwtToken'));
  };

  const hasAgreedTOS = () => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('agreedTOS') === 'true';
  };

  const handleCredentialResponse = useCallback(async (response: any) => {
    try {
      const url = `${process.env.NEXT_PUBLIC_BACKEND_URL}/auth/google/one`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-origin': process.env.NEXT_PUBLIC_BASE_URL || ''
        },
        body: JSON.stringify({ credential: response.credential })
      });
      const data = await res.json();

      if (res.ok && data.token) {
        localStorage.setItem('jwtToken', data.token);
        window.location.reload();
      }
    } catch (err) {
      console.error('Google One Tap authentication failed:', err);
    }
  }, []);

  const initializeOneTap = useCallback(() => {
    if (!window.google || isLoggedIn() || !GOOGLE_CLIENT_ID || !hasAgreedTOS()) {
      return;
    }
    if (promptCalledRef.current) return;
    promptCalledRef.current = true;

    try {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleCredentialResponse,
        context: 'signin',
        ux_mode: 'popup',
        auto_select: false,
        use_fedcm_for_prompt: true
      });

      window.google.accounts.id.prompt();
    } catch (err) {
      console.error('Failed to initialize Google One Tap:', err);
    }
  }, [handleCredentialResponse]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (isScriptLoaded && hasAgreedTOS()) {
        initializeOneTap();
        clearInterval(interval);
      }
    }, 500);

    return () => clearInterval(interval);
  }, [isScriptLoaded, initializeOneTap]);

  useEffect(() => {
    if (isLoggedIn()) {
      window.google?.accounts.id.cancel();
    }
  }, []);

  return (
    <Script
      src="https://accounts.google.com/gsi/client"
      async
      defer
      onLoad={() => setIsScriptLoaded(true)}
      strategy="afterInteractive"
    />
  );
}
